/**
 * Benefits Analytics — HR-admin dashboard tab.
 *
 * Everything here is computed server-side from real enrollment + plan
 * + employee data (see backend benefits_analytics_service). Age/gender
 * bands are intentionally shown as "not available" rather than
 * fabricated — Lenavio doesn't capture employee DOB/gender.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Download,
  PieChart,
  TrendingDown,
  Users,
} from "lucide-react";
import { pageTransition, formatCurrency } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  downloadRenewalReportXlsx,
  getBenefitsAnalytics,
  getRenewalReport,
  type BenefitsAnalytics,
  type RenewalReport,
} from "@/api/benefits";

function Bar({ pct, label, value }: { pct: number; label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-text-primary truncate">{label}</span>
        <span className="text-text-muted ml-2 shrink-0">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-background overflow-hidden">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card variant="flat" className="p-4">
      <p className="text-[11px] uppercase tracking-wide text-text-muted">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold font-heading text-text-primary">
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </Card>
  );
}

export function BenefitsAnalyticsTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<BenefitsAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [renewal, setRenewal] = useState<RenewalReport | null>(null);
  const [loadingRenewal, setLoadingRenewal] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getBenefitsAnalytics());
    } catch {
      addToast("error", "Couldn't load benefits analytics");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRenewal = async () => {
    setLoadingRenewal(true);
    try {
      const r = await getRenewalReport();
      setRenewal(r);
    } catch {
      addToast("error", "Couldn't generate the renewal report");
    } finally {
      setLoadingRenewal(false);
    }
  };

  const handleDownloadXlsx = async () => {
    setDownloadingXlsx(true);
    try {
      await downloadRenewalReportXlsx();
    } catch {
      addToast("error", "Couldn't download the Excel packet");
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const downloadRenewalJson = () => {
    if (!renewal) return;
    const blob = new Blob([JSON.stringify(renewal, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `renewal-prep-${renewal.generated_on}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  const e = data?.enrollment;
  const p = data?.projections;
  const d = data?.demographics;

  if (!e) {
    return (
      <Card className="text-center py-12">
        <PieChart className="h-8 w-8 text-text-muted mx-auto mb-2" />
        <p className="text-text-primary font-medium">No benefits data yet</p>
        <p className="text-sm text-text-muted mt-1">
          Once employees enroll, analytics will appear here.
        </p>
      </Card>
    );
  }

  const maxPlanEnrolled = Math.max(
    1,
    ...e.plan_distribution.map((x) => x.enrolled),
  );
  const maxTier = Math.max(1, ...e.tier_distribution.map((x) => x.count));

  return (
    <motion.div {...pageTransition} className="space-y-6">
      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Enrolled"
          value={`${e.enrolled}/${e.eligible}`}
          sub={`${e.enrollment_rate}% enrollment rate`}
        />
        <StatTile
          label="Waived"
          value={String(e.waived)}
          sub={`${e.waiver_rate}% waiver rate`}
        />
        <StatTile
          label="Total monthly premium"
          value={formatCurrency(e.total_monthly_premium)}
          sub={`Avg ${formatCurrency(e.avg_premium_per_employee)}/employee`}
        />
        <StatTile
          label="Employer monthly"
          value={formatCurrency(e.employer_monthly)}
          sub={
            p ? `${formatCurrency(p.employer_annual_forecast)}/yr forecast` : undefined
          }
        />
      </div>

      {/* Plan + tier distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" /> Plan enrollment
          </h3>
          <div className="space-y-3">
            {e.plan_distribution.length === 0 && (
              <p className="text-sm text-text-muted">No active enrollments.</p>
            )}
            {e.plan_distribution.map((pl) => (
              <Bar
                key={pl.plan_id || pl.plan_name}
                label={pl.plan_name}
                value={`${pl.enrolled} · ${formatCurrency(pl.employer_monthly)}/mo`}
                pct={(pl.enrolled / maxPlanEnrolled) * 100}
              />
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <PieChart className="h-4 w-4" /> Coverage tier mix
          </h3>
          <div className="space-y-3">
            {e.tier_distribution.map((t) => (
              <Bar
                key={t.tier}
                label={t.label}
                value={String(t.count)}
                pct={(t.count / maxTier) * 100}
              />
            ))}
          </div>
        </Card>
      </div>

      {/* Cost projections */}
      {p && (
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4" /> Cost projections
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              label="Employer annual"
              value={formatCurrency(p.employer_annual_forecast)}
            />
            <StatTile
              label="Total annual"
              value={formatCurrency(p.total_annual_forecast)}
            />
            <StatTile
              label="If all on cheapest plan"
              value={formatCurrency(
                p.cheapest_mix.employer_monthly_if_all_cheapest,
              )}
              sub="employer / month"
            />
            <StatTile
              label="Potential annual savings"
              value={formatCurrency(p.cheapest_mix.potential_annual_savings)}
              sub="vs current mix"
            />
          </div>
          <p className="text-xs text-text-muted mt-3">
            {p.cheapest_mix.note}
          </p>
        </Card>
      )}

      {/* Demographics (real) */}
      {d && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-3">
              Department enrollment
            </h3>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-text-muted">
                <tr className="border-b border-border">
                  <th className="py-1.5 pr-2 font-medium">Department</th>
                  <th className="py-1.5 px-2 font-medium text-right">Enrolled</th>
                  <th className="py-1.5 pl-2 font-medium text-right">Waived</th>
                </tr>
              </thead>
              <tbody>
                {d.department_patterns.map((row) => (
                  <tr key={row.department} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-2 text-text-primary">{row.department}</td>
                    <td className="py-1.5 px-2 text-right text-text-primary">{row.enrolled}</td>
                    <td className="py-1.5 pl-2 text-right text-text-muted">{row.waived}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-3">
              Age, sex &amp; tenure
            </h3>

            {/* Age bands */}
            {d.age_bands.available && d.age_bands.bands ? (
              <div className="space-y-2 mb-4">
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Age bands
                </p>
                {(() => {
                  const max = Math.max(
                    1,
                    ...d.age_bands.bands.map((b) => b.count),
                  );
                  return d.age_bands.bands.map((b) => (
                    <Bar
                      key={b.band}
                      label={b.band}
                      value={String(b.count)}
                      pct={(b.count / max) * 100}
                    />
                  ));
                })()}
                {!!d.age_bands.missing_dob && (
                  <p className="text-[11px] text-text-muted">
                    {d.age_bands.missing_dob} enrolled missing DOB —
                    excluded.
                  </p>
                )}
              </div>
            ) : null}

            {/* Sex */}
            {d.sex_bands.available ? (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-wide text-text-muted mb-1">
                  Sex (actuarial)
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="info">Male {d.sex_bands.male ?? 0}</Badge>
                  <Badge variant="info">
                    Female {d.sex_bands.female ?? 0}
                  </Badge>
                  {!!d.sex_bands.not_recorded && (
                    <Badge variant="default">
                      Not recorded {d.sex_bands.not_recorded}
                    </Badge>
                  )}
                </div>
              </div>
            ) : null}

            {/* Tenure */}
            <div className="space-y-2 text-sm">
              <p className="text-xs uppercase tracking-wide text-text-muted">
                Tenure
              </p>
              <div className="flex justify-between">
                <span className="text-text-muted">New hires (&lt;1yr)</span>
                <span className="text-text-primary">
                  {d.tenure.new_hire_under_1yr.enrolled} enrolled ·{" "}
                  {d.tenure.new_hire_under_1yr.waived} waived
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Tenured (1yr+)</span>
                <span className="text-text-primary">
                  {d.tenure.tenured_1yr_plus.enrolled} enrolled ·{" "}
                  {d.tenure.tenured_1yr_plus.waived} waived
                </span>
              </div>
            </div>

            {(!d.age_bands.available || !d.sex_bands.available) && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-text-muted">
                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                <span>
                  {!d.age_bands.available && !d.sex_bands.available
                    ? "Add date of birth and sex on employee profiles to populate age/sex bands for carrier renewals."
                    : !d.age_bands.available
                      ? "Add date of birth on employee profiles to populate age bands."
                      : "Add sex on employee profiles for full actuarial breakdown."}
                </span>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Renewal prep report */}
      <Card>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-text-primary">
            Broker renewal prep report
          </h3>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleRenewal} isLoading={loadingRenewal}>
              Generate
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDownloadXlsx}
              isLoading={downloadingXlsx}
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
            {renewal && (
              <Button size="sm" variant="secondary" onClick={downloadRenewalJson}>
                <Download className="h-3.5 w-3.5 mr-1" /> JSON
              </Button>
            )}
          </div>
        </div>
        {!renewal ? (
          <p className="text-sm text-text-muted">
            Generate the data a broker brings to carrier negotiations:
            enrollment by plan, tier mix, waiver rate, and year-over-year
            change.
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant="info">
                Waiver {renewal.waiver_rate}%
              </Badge>
              <Badge variant="info">
                Enrollment {renewal.enrollment_rate}%
              </Badge>
              <Badge variant="default">
                Employer {formatCurrency(renewal.employer_monthly)}/mo
              </Badge>
              {(renewal.year_over_year as { available?: boolean })
                ?.available ? (
                <Badge variant="success">
                  YoY enrolled{" "}
                  {String(
                    (renewal.year_over_year as { enrolled_change?: number })
                      .enrolled_change,
                  )}
                </Badge>
              ) : (
                <Badge variant="default">YoY: single period only</Badge>
              )}
            </div>
            {renewal.data_gaps?.length > 0 && (
              <ul className="text-xs text-text-muted list-disc ml-4">
                {renewal.data_gaps.map((g, i) => (
                  <li key={i}>{g}</li>
                ))}
              </ul>
            )}
            <p className="text-xs text-text-muted">
              Download the JSON for the full packet to share with your broker.
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
}
